import _ from 'lodash'
import { FTProcess } from '@/models/FTProcess';
import { weightToLength, initProcess, ProcessConfig, ConversionResults, shapeArea, ParsedVariables } from './index'
import { FTProcessRow } from '@/models/FTProcessRow';
import { ConversionDetails } from '@/models/ConversionDetails';
import { convertStep } from './CConvertStep';

export function loadConfig({ process, processRow }: ParsedVariables): ProcessConfig {
  return {
    settings: {
      laminationEnabled: {
        type: 'checkbox',
        name: 'Enable Lamination',
      },
      cuttingEnabled: {
        type: 'checkbox',
        name: 'Enable Die Cutting',
      },
      horizontalGap: {
        type: 'text',
        name: 'Horizontal Gap',
        unit: 'mm',
        showIf: _.get(process, 'settings.cuttingEnabled') === true,
        default: 5
      },
      verticalGap: {
        type: 'text',
        name: 'Vertical Gap',
        unit: 'mm',
        showIf: _.get(process, 'settings.cuttingEnabled') === true,
        default: 5
      }
    },
    inputs: {
      goods: [
        {
          goodsType: 'layer',
          goodsFilter: {
            form: ['roll'],
            family: ['Paper', 'Film']
          },
          multiple: false,
          params: [
            {
              name: 'Width',
              type: 'label',
              value: _.get(processRow, 'goodsObject.dimensions.width', ''),
              unit: 'mm',
              disabled: true
            },
            {
              name: 'Quantity',
              type: 'quantity',
              value: 'quantity',
              disabled: false
            }
          ]
        },
        {
          goodsType: 'ink',
          goodsFilter: {
            type: ['ORM'],
            family: ['Chemical'],
            category: ['Ink']
          },
          multiple: true,
          addMultipleButtonText: 'add more inks',
          params: [
            {
              name: 'Quantity',
              type: 'quantity',
              value: 'quantity',
              disabled: false
            }
          ]
        },
        {
          goodsType: 'adhesive',
          goodsFilter: {
            type: ['ORM'],
            family: ['Chemical'],
            category: ['Adhesive']
          },
          multiple: false,
          showIf: _.get(process, 'settings.laminationEnabled') === true,
          params: [
            {
              name: 'Linear Length',
              type: 'label',
              value: false,
              disablePrint: true,
              disabled: true
            },
            {
              name: 'Quantity',
              type: 'quantity',
              value: 'quantity',
              disabled: false
            }
          ]
        },
        {
          goodsType: 'lamination',
          goodsFilter: {
            form: ['roll'],
            family: ['Film', 'Paper']
          },
          multiple: false,
          showIf: _.get(process, 'settings.laminationEnabled') === true,
          params: [
            {
              name: 'Width',
              type: 'label',
              value: _.get(processRow, 'goodsObject.dimensions.width', ''),
              unit: 'mm',
              disabled: true
            },
            {
              name: 'Quantity',
              type: 'quantity',
              value: 'quantity',
              disabled: false
            }
          ]
        }
      ]
    },
    outputs: {
      type: 'single',
      arguments: [
        {
          name: 'Shape',
          type: 'shape',
          value: 'shape',
          optionsArray: [ 'Round', 'Oval', 'Rectangle', 'Rectangle+Oval', 'Other'],
          showIf: _.get(processRow, 'ftProcess.settings.cuttingEnabled') === true,
          disabled: false
        },
        {
          name: 'Area',
          type: 'dimension',
          value: 'area',
          unit: 'sq. mm',
          showIf: _.get(processRow, 'dimensions.shape') === 'Other',
          disabled: false
        },
        {
          name: 'Diameter',
          type: 'dimension',
          value: 'diameter',
          unit: 'mm',
          showIf: _.get(processRow, 'dimensions.shape') === 'Round',
          disabled: false
        },
        {
          name: 'Width',
          type: 'dimension',
          value: 'width',
          unit: 'mm',
          showIf: ['Oval', 'Rectangle', 'Rectangle+Oval'].includes(_.get(processRow, 'dimensions.shape')),
          disabled: false
        },
        {
          name: 'Length',
          type: 'dimension',
          value: 'length',
          unit: 'mm',
          showIf: ['Oval', 'Rectangle', 'Rectangle+Oval'].includes(_.get(processRow, 'dimensions.shape')),
          disabled: false
        }
      ]
    },
    tableHeaders: [
      {
        text: 'Nominal Speed',
        value: 'nominalSpeed'
      },
      {
        text: 'Planned Hours(s)',
        value: 'plannedHours'
      },
      {
        text: 'Linear Length',
        value: 'linearMeters'
      },
      {
        text: 'Expected Output Waste',
        value: 'wastePercentage'
      },
      {
        text: 'Expected Output Waste Weight',
        value: 'waste'
      },
      {
        text: 'Expected Output Net Weight',
        value: 'netWeight'
      }
    ]
  }
}

export function convert(process: FTProcess) : ConversionResults {
  const conversionResult : ConversionResults = {
    inputs: [],
    outputs: [],
    details: new ConversionDetails(),
  };

  initProcess(process);

  let linearMeters = 0, netWeight = 0;
  const layerInputProcessRow = process.inputs[0]
  let outputFTProcessRow = process.outputs[0];
  conversionResult.outputs[0] = outputFTProcessRow;

  if (layerInputProcessRow.goodsType == 'layer' && layerInputProcessRow.goodsObject.dimensions) {
    linearMeters = weightToLength(layerInputProcessRow.quantity, layerInputProcessRow.goodsObject.density, layerInputProcessRow.goodsObject.dimensions.width);
    outputFTProcessRow.goodsObject.copyFrom(layerInputProcessRow.goodsObject);
  }
  if(process.settings.laminationEnabled) {
    const adhesiveIndex =_.findIndex(process.inputs, { goodsType: 'adhesive' })
    const laminationIndex =_.findIndex(process.inputs, { goodsType: 'lamination' })
    if(adhesiveIndex > -1 && laminationIndex > -1) {
      const adhesiveInputProcessRow = process.inputs[adhesiveIndex]
      const laminationInputProcessRow = process.inputs[laminationIndex]
      outputFTProcessRow = convertStep.lamination(layerInputProcessRow, laminationInputProcessRow, adhesiveInputProcessRow)
      outputFTProcessRow.goodsObject.form = 'roll'
    }
  }
  _.mapValues(process.inputs, (processInputRow: FTProcessRow) => {
    netWeight += (processInputRow.quantity || 0);
  });
  if(process.settings.cuttingEnabled && outputFTProcessRow.goodsObject.dimensions) {
    outputFTProcessRow.goodsObject.form = 'die-cut-sheet';
    const printedRollLinearLengthInMm = weightToLength(netWeight, outputFTProcessRow.goodsObject.density, outputFTProcessRow.goodsObject.dimensions.width) * 1000;
    const totalAreaInM2 = (printedRollLinearLengthInMm * outputFTProcessRow.goodsObject.dimensions.width) / 1000000;
    const { diameter, width, length } = outputFTProcessRow.dimensions;
    const dieCutSheetWidthWithGap = (diameter || width) + process.settings.horizontalGap;
    const dieCutSheetLengthWithGap = (diameter || length) + process.settings.verticalGap;
    const noOfPieces = totalAreaInM2/((dieCutSheetLengthWithGap * dieCutSheetWidthWithGap) / 1000000);
    outputFTProcessRow.goodsObject.unitWeight = (outputFTProcessRow.goodsObject.density * shapeArea({
      shape: outputFTProcessRow.dimensions.shape,
      params: outputFTProcessRow.dimensions,
      outUnit: 'm'
    })) / 1000;
    outputFTProcessRow.quantity = outputFTProcessRow.goodsObject.unitWeight * noOfPieces;
    const pcsPerKg = noOfPieces / outputFTProcessRow.quantity;
    outputFTProcessRow.goodsObject.setPerKgRatio('plA', pcsPerKg);
    if (outputFTProcessRow.dimensions) {
      outputFTProcessRow.goodsObject.dimensions = outputFTProcessRow.dimensions;
    }
  } else {
    outputFTProcessRow.quantity = netWeight;
  }
  conversionResult.details.totalQuantity = netWeight;
  conversionResult.details.linearMeters = linearMeters;
  conversionResult.details.wastePercentage = ((netWeight - outputFTProcessRow.quantity) / netWeight) * 100;
  conversionResult.details.waste = netWeight - outputFTProcessRow.quantity;
  conversionResult.details.netWeight = outputFTProcessRow.quantity;
  conversionResult.details.kgPerHour = 60 * (process.machineDetail.mechanicalSpeed / linearMeters) * conversionResult.details.totalQuantity;
  conversionResult.details.plannedHours = conversionResult.details.totalQuantity / conversionResult.details.kgPerHour;

  return conversionResult;
}